// Los diálogos de la app: confirmar, pedir un texto y avisar.
//
// Existía un ConfirmProvider escrito y NUNCA MONTADO, así que toda la app seguía usando los
// `confirm()` y `prompt()` del navegador: cajas grises del sistema, con el dominio en la cabecera,
// imposibles de estilar y que en el iPad salen como un aviso de página web y no como parte del
// programa. Además `prompt()` no tenía sustituto ninguno, así que numeraciones de factura y nombres
// de series se tecleaban ahí dentro.
//
// DOS FORMAS DE USARLO, a propósito:
//   1. `import { confirmar, pedirTexto, avisar } from '../context/DialogosContext'` — funciones
//      normales que devuelven una promesa. Es la vía principal: la mitad de los `confirm()` de la app
//      viven en funciones sueltas o callbacks donde no se puede llamar a un hook.
//   2. `const { confirmar } = useDialogos()` — si prefieres el hook dentro de un componente.
// Las dos hablan con el MISMO diálogo montado una sola vez en App.
//
// Si por lo que sea el proveedor no estuviera montado, se cae al diálogo del navegador. Devolver
// `false` a secas sería peor: cancelaría en silencio acciones que el usuario sí quería hacer.
import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
// B-735 · un clic en el fondo de una ventana con un formulario no la cierra. Se instala en este proveedor porque envuelve
// la aplicación entera y es el que sabe preguntar cuando una ventana no tiene a la vista cómo cerrarse.
import { instalarFondoDeModal } from '../utils/fondoDeModal'
// L21 (4-oct-2026) · la casilla oculta del usuario y lo que de verdad hay en la de la contraseña (utils/reautenticar).
import { usuarioDeLaSesion, CONTRASENA_ACTUAL } from '../utils/reautenticar'
import { t as i18nT } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

const DialogosContext = createContext()

// Puente entre las funciones sueltas y el proveedor montado.
let _abrir = null

function pedirAlProveedor(dialogo) {
  if (_abrir) return _abrir(dialogo)
  // Sin proveedor montado: el navegador. Nunca debería pasar, pero no se pierde la acción.
  if (dialogo.clase === 'texto') return Promise.resolve(window.prompt(dialogo.mensaje, dialogo.valor || ''))
  if (dialogo.clase === 'aviso') { window.alert(dialogo.mensaje); return Promise.resolve() }
  return Promise.resolve(window.confirm(dialogo.mensaje))
}

const normalizar = (o) => (typeof o === 'string' ? { mensaje: o } : (o || {}))

// ¿Seguimos? → Promise<boolean>
export function confirmar(opciones) {
  return pedirAlProveedor({ clase: 'confirmar', ...normalizar(opciones) })
}
// Pide un texto → Promise<string|null> (null si cancela, igual que prompt())
export function pedirTexto(opciones) {
  return pedirAlProveedor({ clase: 'texto', ...normalizar(opciones) })
}
// Informa y espera al OK → Promise<void>
export function avisar(opciones) {
  return pedirAlProveedor({ clase: 'aviso', ...normalizar(opciones) })
}

export function DialogosProvider({ children }) {
  const [cola, setCola] = useState([])
  const abrir = useCallback((dialogo) => new Promise((resolve) => {
    setCola(c => [...c, { ...dialogo, resolve, id: Math.random().toString(36).slice(2) }])
  }), [])

  // Registrar el puente mientras este proveedor esté vivo.
  useEffect(() => { _abrir = abrir; return () => { _abrir = null } }, [abrir])

  // B-735 · LA VENTANA CON UN FORMULARIO NO SE CIERRA AL PINCHAR FUERA (utils/fondoDeModal).
  useEffect(() => instalarFondoDeModal(typeof window !== 'undefined' ? window : null, {
    preguntar: () => confirmar({
      titulo: i18nT('context.DialogosContext.cerrar_esta_ventana', 'Cerrar esta ventana'),
      mensaje: i18nT('context.DialogosContext.lo_que_hayas_escrito_en', 'Lo que hayas escrito en ella se perderá.'),
      textoConfirmar: i18nT('context.DialogosContext.cerrar', 'Cerrar'), textoCancelar: i18nT('context.DialogosContext.seguir_aqui', 'Seguir aquí'),
    }),
  }), [])

  const responder = (valor) => {
    setCola(c => {
      const [actual, ...resto] = c
      if (actual?.resolve) actual.resolve(valor)
      return resto
    })
  }

  const api = { confirmar, pedirTexto, avisar }
  const actual = cola[0]

  return (
    <DialogosContext.Provider value={api}>
      {children}
      {actual && <Dialogo key={actual.id} d={actual} onResponder={responder} />}
    </DialogosContext.Provider>
  )
}

const COLORES = {
  danger: 'var(--danger, #dc2626)',
  warning: 'var(--warning, #d97706)',
  info: 'var(--primary, #2563eb)',
}

function Dialogo({ d, onResponder }) {
  const esTexto = d.clase === 'texto'
  const esAviso = d.clase === 'aviso'
  const [valor, setValor] = useState(d.valor != null ? String(d.valor) : '')
  const inputRef = useRef(null)
  // t251 (15-oct-2026) · EL OJO DE LA CONTRASEÑA. El dueño: «al poner la cuenta del panel de referidores, que haya un botón
  // para ver la contraseña». Enseña u oculta SOLO lo que se está escribiendo en esta casilla: la contraseña guardada no la
  // tiene la pantalla y no se enseña nunca.
  const [verContrasena, setVerContrasena] = useState(false)
  const acento = COLORES[d.tipo] || (d.clase === 'confirmar' ? COLORES.warning : COLORES.info)

  useEffect(() => {
    if (!esTexto) return
    const t = setTimeout(() => { inputRef.current?.focus(); inputRef.current?.select?.() }, 40)
    return () => clearTimeout(t)
  }, [esTexto])

  const cancelar = () => onResponder(esTexto ? null : false)
  // t513 (B-2246) · `pausaMs` (opcional): en sus primeros N ms, ni el botón de aceptar (ni Enter) ni el clic en el fondo
  // responden. Es para la pregunta de llamar (utils/llamar): con un doble clic en «Llamar», el segundo clic cae ya sobre el
  // diálogo y lo cerraba (en el fondo) o llamaba sin haberlo leído (en su botón). «Cancelar» y Escape valen siempre. Sin
  // `pausaMs`, como siempre.
  const abiertoEn = useRef(Date.now())
  const enPausa = () => Number(d.pausaMs) > 0 && Date.now() - abiertoEn.current < Number(d.pausaMs)
  const cancelarDesdeElFondo = () => { if (!enPausa()) cancelar() }
  // L21 · LA CONTRASEÑA QUE PONE EL LLAVERO DEL IPHONE: está en la casilla aunque React no se haya enterado. Lo tecleado
  // manda; si el estado está vacío, se lee la casilla.
  const esContrasena = esTexto && !d.multilinea && d.tipoCampo === 'password'
  const aceptar = () => {
    if (enPausa()) return
    if (esContrasena) {
      const enLaCasilla = inputRef.current && typeof inputRef.current.value === 'string' ? inputRef.current.value : ''
      return onResponder(valor || enLaCasilla)
    }
    if (esTexto) return onResponder(valor)
    onResponder(esAviso ? undefined : true)
  }

  // Escape cierra, Enter acepta (en el campo de texto de una línea).
  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.stopPropagation(); cancelar() }
    if (e.key === 'Enter' && (!esTexto || !d.multilinea)) { e.preventDefault(); aceptar() }
  }

  return (
    <div className="modal-safe" onMouseDown={cancelarDesdeElFondo} onKeyDown={onKeyDown} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99998,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      animation: 'dlgFade .15s',
    }}>
      {/* CABECERA Y PIE QUIETOS, CUERPO CON SU PROPIA BARRA. Un diálogo con un mensaje largo (un
          detalle de varias líneas, el motivo de un error) crecía hacia abajo hasta salirse del
          teléfono, y el botón de confirmar quedaba fuera de la pantalla sin forma de llegar: el
          overlay es `position: fixed`, así que la página de detrás tampoco se desplaza. La regla
          común de `index.css` (`.modal-safe > *`) ya deja desplazar cualquier tarjeta, pero aquí
          interesa algo mejor: que el título y los botones no se muevan y sea el texto el que corre.
          `minHeight: 0` en el cuerpo es lo que permite que encoja: un hijo de flex, por defecto, no
          baja de lo que mide su contenido y entonces la barra no aparece nunca. */}
      <div onMouseDown={e => e.stopPropagation()} role="dialog" aria-modal="true"
        aria-labelledby={'dlg-titulo-' + d.id} aria-describedby={d.mensaje ? 'dlg-mensaje-' + d.id : undefined} style={{
        background: 'var(--surface, #fff)', color: 'var(--text-primary, #0f1923)',
        borderRadius: 14, maxWidth: esTexto ? 520 : 460, width: '100%',
        border: '1px solid var(--border, #e8ecf0)', borderTop: `4px solid ${acento}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', animation: 'dlgIn .2s ease-out',
        display: 'flex', flexDirection: 'column', maxHeight: '100%', overflow: 'hidden',
      }}>
        <h3 id={'dlg-titulo-' + d.id} style={{ margin: 0, padding: '20px 22px 8px', fontSize: 17, fontWeight: 800, color: acento, flexShrink: 0 }}>
          {d.titulo || (esTexto ? i18nT('context.DialogosContext.escribe_un_valor', 'Escribe un valor') : esAviso ? i18nT('context.DialogosContext.aviso', 'Aviso') : i18nT('context.DialogosContext.confirmar_accion', 'Confirmar acción'))}
        </h3>
        <div style={{ padding: '0 22px 14px', overflowY: 'auto', overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', minHeight: 0 }}>
          {d.mensaje && (
            <div id={'dlg-mensaje-' + d.id} style={{ fontSize: 14, color: 'var(--text-secondary, #374151)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {d.mensaje}
            </div>
          )}
          {d.detalle && (
            <div style={{
              marginTop: 10, padding: '10px 12px', background: 'var(--surface-2, #f9fafb)',
              borderRadius: 8, fontSize: 13, color: 'var(--text-secondary, #4b5563)',
              whiteSpace: 'pre-wrap', borderLeft: `3px solid ${acento}`,
            }}>{d.detalle}</div>
          )}
          {/* LA MINIATURA DE LO QUE SE VA A GUARDAR. Un diálogo que dice "se adjuntará la imagen"
              sin enseñarla obliga a fiarse: con veinte fotos en la conversación, la que se anota
              acaba siendo la de al lado. `imagen` es una URL ya resuelta por quien abre el diálogo
              (con su token); si no se pasa, esto no existe y el diálogo es el de siempre. */}
          {d.imagen && (
            <img src={d.imagen} alt="" style={{
              marginTop: 10, display: 'block', maxWidth: '100%', maxHeight: 220,
              borderRadius: 8, border: '1px solid var(--border, #e8ecf0)', objectFit: 'contain',
            }} />
          )}
          {esTexto && (
            d.multilinea ? (
              <textarea ref={inputRef} value={valor} onChange={e => setValor(e.target.value)}
                placeholder={d.placeholder || ''} rows={4} style={campoEstilo} />
            ) : esContrasena ? (
              // L21 · «CONFIRMA QUE ERES TÚ»: un formulario de verdad y, cuando es la contraseña de ESTA sesión (quien llama
              // pasa `usuario: true`, o el correo), la casilla oculta de su usuario, para que el llavero del iPhone ofrezca
              // esa contraseña. Sin `usuario` es la de OTRA cuenta (el portal del paciente o el de referidores): no se pone
              // el usuario de la sesión, que haría ofrecer la contraseña equivocada.
              <form data-dialogo-contrasena="si" onSubmit={e => { e.preventDefault(); aceptar() }} style={{ margin: 0 }}>
                {d.usuario && <input {...usuarioDeLaSesion(typeof d.usuario === 'string' ? d.usuario : '')} />}
                <div style={{ position: 'relative' }}>
                  <input ref={inputRef} value={valor} onChange={e => setValor(e.target.value)} type={verContrasena ? 'text' : 'password'}
                    {...CONTRASENA_ACTUAL} autoComplete={d.usuario ? 'current-password' : 'off'}
                    placeholder={d.placeholder || ''} style={{ ...campoEstilo, paddingRight: 46 }} />
                  <button type="button" data-ver-contrasena={verContrasena ? 'si' : 'no'} onClick={() => setVerContrasena(v => !v)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') e.stopPropagation() }}
                    aria-label={verContrasena ? i18nT('context.DialogosContext.ocultar_la_contrasena', 'Ocultar la contraseña') : i18nT('context.DialogosContext.mostrar_la_contrasena', 'Mostrar la contraseña')} aria-pressed={verContrasena}
                    title={verContrasena ? i18nT('context.DialogosContext.ocultar_la_contrasena', 'Ocultar la contraseña') : i18nT('context.DialogosContext.mostrar_la_contrasena', 'Mostrar la contraseña')}
                    style={{ position: 'absolute', right: 4, top: 'calc(50% + 6px)', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', width: 38, height: 34, background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer',
                      color: 'var(--text-muted, #6b7280)', padding: 0 }}>
                    <OjoContrasena abierto={verContrasena} />
                  </button>
                </div>
                {/* t251 · «y restablecerla si no me acuerdo»: quien abre el diálogo dice adónde se va (`alOlvidar`). Cierra el
                    diálogo como «Cancelar» (la promesa da null) y después lleva al camino de restablecerla. */}
                {d.textoOlvido && typeof d.alOlvidar === 'function' && (
                  <button type="button" data-olvido-contrasena="" onClick={() => { onResponder(null); d.alOlvidar() }}
                    style={{ marginTop: 10, background: 'none', border: 'none', padding: 0, fontFamily: 'inherit', fontSize: 13, fontWeight: 700,
                      color: 'var(--enlace, var(--primary, #2563eb))', cursor: 'pointer', textAlign: 'left' }}>
                    {d.textoOlvido}
                  </button>
                )}
              </form>
            ) : (
              <input ref={inputRef} value={valor} onChange={e => setValor(e.target.value)}
                type={d.tipoCampo || 'text'} placeholder={d.placeholder || ''} style={campoEstilo} />
            )
          )}
        </div>
        <div style={{ padding: '4px 22px 20px', display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', flexShrink: 0 }}>
          {!esAviso && (
            <button onClick={cancelar} style={{
              padding: '10px 18px', background: 'var(--surface-2, #f3f4f6)',
              border: '1px solid var(--border, #d1d5db)', borderRadius: 8, fontSize: 14,
              fontWeight: 600, color: 'var(--text-primary, #374151)', cursor: 'pointer',
            }}>{d.textoCancelar || i18nT('context.DialogosContext.cancelar', 'Cancelar')}</button>
          )}
          {/* TERCERA acción opcional: resuelve con la cadena 'extra' en vez de con true/false, para
              distinguirla de aceptar y de cancelar. Hace falta cuando el diálogo ofrece una SALIDA
              (p. ej. "Ir al consentimiento") que no es ni continuar ni quedarse: sin ella habría que
              colgarla del botón de cancelar, y entonces cerrar con Escape o pulsando fuera —que también
              resuelven cancelar— dispararía esa salida sin que nadie la haya pedido. */}
          {!esAviso && d.textoExtra && (
            <button onClick={() => onResponder('extra')} style={{
              padding: '10px 18px', background: 'transparent',
              border: `1px solid ${acento}`, borderRadius: 8, fontSize: 14,
              fontWeight: 700, color: acento, cursor: 'pointer',
            }}>{d.textoExtra}</button>
          )}
          {/* B-2232 · Y UNA CUARTA, igual que la tercera pero resolviendo 'extra2' (la pregunta del consentimiento al dar la
              cita: enviarlo, crear la cita sin enviarlo, crear la cita sin consentimiento o volver y corregir). */}
          {!esAviso && d.textoExtra2 && (
            <button onClick={() => onResponder('extra2')} style={{
              padding: '10px 18px', background: 'transparent',
              border: '1px solid var(--border, #d1d5db)', borderRadius: 8, fontSize: 14,
              fontWeight: 700, color: 'var(--text-primary, #374151)', cursor: 'pointer',
            }}>{d.textoExtra2}</button>
          )}
          <button onClick={aceptar} autoFocus={!esTexto} style={{
            padding: '10px 18px', background: acento, border: 'none', borderRadius: 8,
            fontSize: 14, fontWeight: 700, color: '#fff', cursor: 'pointer',
          }}>{d.textoConfirmar || (esTexto ? i18nT('context.DialogosContext.guardar', 'Guardar') : esAviso ? i18nT('context.DialogosContext.entendido', 'Entendido') : i18nT('context.DialogosContext.confirmar', 'Confirmar'))}</button>
        </div>
        <style>{`
          @keyframes dlgFade { from { opacity: 0 } to { opacity: 1 } }
          @keyframes dlgIn { from { opacity: 0; transform: translateY(-20px) scale(.95) } to { opacity: 1; transform: none } }
        `}</style>
      </div>
    </div>
  )
}

// t251 · el ojo de la casilla de la contraseña: abierto (se ve lo escrito) o tachado (oculto). Un dibujo, sin texto.
function OjoContrasena({ abierto }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
      <circle cx="12" cy="12" r="3" />
      {!abierto && <path d="M4 4l16 16" />}
    </svg>
  )
}

const campoEstilo = {
  width: '100%', marginTop: 12, padding: '10px 12px', fontSize: 14,
  border: '1px solid var(--border, #d1d5db)', borderRadius: 8,
  background: 'var(--surface-2, #fff)', color: 'var(--text-primary, #0f1923)',
  fontFamily: 'inherit', boxSizing: 'border-box',
}

export function useDialogos() {
  const ctx = useContext(DialogosContext)
  return ctx || { confirmar, pedirTexto, avisar }
}

// Compatibilidad con el nombre viejo: `const confirmar = useConfirm()` seguía siendo un hook que
// devolvía la función suelta.
export function useConfirm() {
  return confirmar
}

export default DialogosProvider
